import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import {
  adminGetApplication,
  adminFileUrl,
  getToken,
  clearSession,
} from "@/lib/api";

export default function AdminDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    adminGetApplication(id)
      .then(setApp)
      .catch((err) => {
        if (err?.response?.status === 401) {
          clearSession();
          navigate("/admin/login");
        } else {
          toast.error("No se pudo cargar la inscripción");
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-eje/[0.03]">
        <Header variant="light" />
        <main className="container-wide py-16 text-center text-slate-500">
          Cargando…
        </main>
      </div>
    );
  }

  if (!app) return null;

  return (
    <div className="min-h-screen bg-eje/[0.03]">
      <Header variant="light" />
      <main className="container-wide py-10">
        <Link
          to="/admin/dashboard"
          className="inline-flex items-center gap-2 text-eje hover:text-gold-600 text-sm mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al panel
        </Link>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="overline text-eje mb-2">Detalle de inscripción</div>
            <h1
              className="font-serif text-3xl md:text-4xl text-eje"
              data-testid="application-folio"
            >
              {app.folio}
            </h1>
            <p className="text-sm text-slate-600 mt-1 capitalize">
              {app.modalidad.replace(/_/g, " ")}
              {app.linea_investigacion ? ` · ${app.linea_investigacion.replace(/_/g, " ")}` : ""}
            </p>
          </div>
          <span
            className={`pill border ${
              app.status === "enviada"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-50 text-amber-700 border-amber-200"
            }`}
          >
            {app.status === "enviada" ? "Enviada" : "Borrador"}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Datos personales */}
          <Section title="Datos personales" className="lg:col-span-2">
            <KV label="Nombre completo" value={`${app.nombre} ${app.apellido_paterno} ${app.apellido_materno || ""}`} />
            <KV label="Género" value={app.sexo} cap />
            <KV label="Fecha de nacimiento" value={app.fecha_nacimiento} />
            <KV label="Nacionalidad" value={app.nacionalidad} />
            <KV label="Identificación oficial" value={app.identificacion_numero} />
            <KV label="Correo" value={app.email} />
            <KV label="Teléfono" value={app.telefono} />
            <KV label="País" value={app.pais} />
            <KV label="Ciudad" value={app.ciudad} />
            <KV label="Dirección" value={app.direccion} />
          </Section>

          {/* Sidebar meta */}
          <Section title="Información de envío">
            <KV
              label="Fecha de registro"
              value={new Date(app.created_at).toLocaleString("es-MX")}
            />
            {app.submitted_at && (
              <KV
                label="Fecha de envío"
                value={new Date(app.submitted_at).toLocaleString("es-MX")}
              />
            )}
            <KV label="Documentos" value={`${app.documents?.length || 0}`} />
            <KV label="Modalidad" value={app.modalidad.replace(/_/g, " ")} cap />
            {app.modalidad_presencial && (
              <KV label="Modo" value={app.modalidad_presencial} cap />
            )}
            <KV
              label="Línea"
              value={(app.linea_investigacion || "—").replace(/_/g, " ")}
              cap
            />
          </Section>

          {/* Datos académicos */}
          <Section title="Datos académicos" className="lg:col-span-3">
            <KV label="Grado máximo" value={app.grado_maximo} cap />
            <KV label="Título" value={app.titulo_obtenido} />
            <KV label="Institución" value={app.institucion} />
            <KV label="Año titulación" value={app.anio_titulacion} />
            <KV label="Cédula profesional" value={app.cedula_profesional || "—"} />
            <KV label="Área de conocimiento" value={app.area_conocimiento} />
          </Section>

          {/* Documentos */}
          <Section title="Documentos cargados" className="lg:col-span-3">
            <div className="col-span-full">
              {!app.documents || app.documents.length === 0 ? (
                <p className="text-sm text-slate-500 py-3">
                  No hay documentos cargados.
                </p>
              ) : (
                <ul className="divide-y divide-slate-200">
                  {app.documents.map((d) => (
                    <li
                      key={d.storage_path}
                      className="py-3 flex items-center justify-between gap-4"
                      data-testid={`doc-${d.doc_key}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 bg-eje/5 border border-eje/10 text-eje flex items-center justify-center rounded-sm flex-shrink-0">
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 capitalize">
                            {d.doc_key.replace(/_/g, " ")}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {d.original_filename} ·{" "}
                            {(d.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <a
                          href={adminFileUrl(d.storage_path)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-300 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
                          data-testid={`view-doc-${d.doc_key}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Ver
                        </a>
                        <a
                          href={adminFileUrl(d.storage_path)}
                          download={d.original_filename}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-eje text-white rounded-sm hover:bg-eje-800 transition-colors"
                          data-testid={`download-doc-${d.doc_key}`}
                        >
                          <Download className="w-3.5 h-3.5" /> Descargar
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Section>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children, className = "" }) {
  return (
    <section className={`card-eje p-6 ${className}`}>
      <h2 className="overline text-eje mb-4">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
        {children}
      </div>
    </section>
  );
}

function KV({ label, value, cap = false }) {
  return (
    <div>
      <div className="overline text-slate-500 mb-1">{label}</div>
      <div
        className={`text-sm text-slate-800 break-words ${
          cap ? "capitalize" : ""
        }`}
      >
        {value || "—"}
      </div>
    </div>
  );
}
